import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  FormFeedback,
  Badge,
  InputGroup,
  InputGroupText,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from 'classnames';
import { Plus, Save, Send, X, AlertCircle } from "lucide-react";
import ProductCard from "../../Components/ProductCard";
import planOptionalsExample from "../../assets/images/PartnerPlansDraft/Plan Optionals Example.png";
import logo from "../../assets/images/whitelogo.png";
import ReactMarkdown from 'react-markdown';

const baseUrl = process.env.REACT_APP_BASE_URL;

interface DraftProduct {
  id?: number;
  product_type: string;
  title: string;
  description: string;
  features: string[];
  price: number | null;
  is_fixed_price: boolean;
  subscription_period: string;
  cancellation_days: number;
  quantifiable: boolean;
  share_percentage: number;
  group?: string;
  priority: number;
}

interface Submission {
  id: number;
  partner_name: string;
  domain_name: string;
  status: string;
  is_locked: boolean;
  expires_at: string;
  days_remaining: number;
}

const PRODUCT_TYPES = [
  { value: "plan", label: "Plan", description: "Main subscription plans with recurring billing. Example: Basic Plan, Premium Plan, Enterprise Plan" },
  { value: "subscriptionaddon", label: "Subscription Add-On", description: "Additional features that can be added to plans with recurring billing. Example: Extra Storage, Advanced Analytics, Priority Support" },
  { value: "planoptionals", label: "Plan Optionals", description: "Optional features grouped together that customers can choose from. Example: Processing Speed Group (options: Slow, Medium, Fast)" },
  { value: "addon", label: "Add-On", description: "One-time add-ons that enhance existing products. Example: Setup Fee, Training Session, Migration Service" },
  { value: "onetimeproduct", label: "One-Time Product", description: "Products purchased once without recurring charges and outisde a plan. Example: One-time Consultation, Custom Report, Implementation Package" },
];

const SUBSCRIPTION_PERIODS = [
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
];

// Preview types matching PackagesPricing structure
type PreviewAddOn = {
  id: string;
  title: string;
  description: string;
  price: string | null;
  validity?: string;
  features: string[];
  selected: boolean;
};

type PreviewSubscriptionAddOn = {
  id: string;
  title: string;
  description: string;
  price: string | null;
  period: string;
  features: string[];
  selected: boolean;
};

type PreviewPlanOptionals = {
  id: string;
  group: string;
  title: string;
  description: string;
  price: string | null;
  validity?: string;
  features: string[];
  selected: boolean;
};

type PreviewPlan = {
  id: string;
  title: string;
  description: string;
  price: string | null;
  validity?: string;
  features: string[];
  addons: PreviewAddOn[];
  subscriptionAddOns: PreviewSubscriptionAddOn[];
  optionals: PreviewPlanOptionals[];
};

type PreviewOneTimeProduct = {
  id: string;
  title: string;
  description: string;
  price: string | null;
  quantifiable: boolean;
  features: string[];
};

const getPlanTotal = (plan: PreviewPlan) => {
  if (plan.price === null || plan.price === undefined) {
    return null;
  }
  const hasNullAddon = plan.addons.some(a => a.selected && (a.price === null || a.price === undefined));
  const hasNullOptional = plan.optionals.some(o => o.selected && (o.price === null || o.price === undefined));
  const hasNullSubAddon = plan.subscriptionAddOns.some(a => a.selected && (a.price === null || a.price === undefined));

  if (hasNullAddon || hasNullOptional || hasNullSubAddon) {
    return null;
  }

  let total = parseFloat(plan.price) || 0;
  plan.addons.forEach(a => { if (a.selected) total += parseFloat(a.price || "0") || 0; });
  plan.optionals.forEach(o => { if (o.selected) total += parseFloat(o.price || "0") || 0; });
  plan.subscriptionAddOns.forEach(a => { if (a.selected) total += parseFloat(a.price || "0") || 0; });
  return total.toFixed(2);
};

const getPlanRecurring = (plan: PreviewPlan) => {
  let recurring: { [period: string]: number } = {};
  
  // Include plan's price if plan is subscription-based
  if (plan.validity && plan.validity !== '' && plan.validity !== 'lifetime' && plan.validity !== 'custom' && plan.price !== null && plan.price !== undefined) {
    const period = plan.validity;
    recurring[period] = (recurring[period] || 0) + (parseFloat(plan.price) || 0);
  }
  
  plan.subscriptionAddOns.forEach(a => {
    if (a.selected && a.price !== null && a.price !== undefined) {
      recurring[a.period] = (recurring[a.period] || 0) + (parseFloat(a.price) || 0);
    }
  });
  
  // Note: Addons are NOT included here because they are one-time purchases with validity periods,
  // not subscription-based recurring charges
  
  plan.optionals.forEach(o => {
    if (o.selected && o.validity && o.validity !== '' && o.validity !== 'lifetime' && o.validity !== 'custom' && o.price !== null && o.price !== undefined) {
      const period = o.validity;
      recurring[period] = (recurring[period] || 0) + (parseFloat(o.price) || 0);
    }
  });
  return recurring;
};

// Helper function to clean markdown content
const cleanMarkdown = (text: string | null | undefined): string => {
  if (!text) return '';
  // Convert to string
  let cleaned = String(text);
  
  // Replace HTML entities if any (do this first)
  cleaned = cleaned.replace(/&amp;/g, '&')
                   .replace(/&lt;/g, '<')
                   .replace(/&gt;/g, '>')
                   .replace(/&quot;/g, '"')
                   .replace(/&#39;/g, "'")
                   .replace(/&ast;/g, '*');
  
  // Preserve newlines that are immediately before markdown syntax (**, *, __, etc.)
  // We use placeholders to protect them from being converted later
  // First, save double newlines (paragraph breaks)
  cleaned = cleaned.replace(/\n\n/g, '{{PARAGRAPH_BREAK}}');
  
  // Then, save newlines before markdown syntax using a placeholder
  // Pattern: newline followed by optional whitespace and markdown syntax
  cleaned = cleaned.replace(/\n(?=\s*[#*_`\-\[\]])/g, '{{MARKDOWN_NEWLINE}}');
  
  // Now convert remaining single newlines to markdown line breaks (two spaces + newline)
  cleaned = cleaned.replace(/\n/g, '  \n');
  
  // Restore the preserved newlines
  cleaned = cleaned.replace(/{{PARAGRAPH_BREAK}}/g, '\n\n');  // Restore double newlines
  cleaned = cleaned.replace(/{{MARKDOWN_NEWLINE}}/g, '\n');   // Restore newlines before markdown
  
  // Fix trailing spaces in markdown syntax (e.g., "**text **" -> "**text**")
  // This handles bold (**text **), italic (*text *), and other markdown patterns
  cleaned = cleaned.replace(/\*\*([^*]+?)\s+\*\*/g, '**$1**')  // Bold with trailing space
                   .replace(/\*([^*]+?)\s+\*/g, '*$1*')        // Italic with trailing space
                   .replace(/__([^_]+?)\s+__/g, '__$1__')       // Bold underscore with trailing space
                   .replace(/_([^_]+?)\s+_/g, '_$1_')          // Italic underscore with trailing space
                   .replace(/~~([^~]+?)\s+~~/g, '~~$1~~');      // Strikethrough with trailing space
  
  return cleaned;
};

const PartnerPlanForm = () => {
  const { token } = useParams<{ token: string }>();
  document.title = "Partner Plan Submission | BizServe";

  const [submission, setSubmission] = useState<Submission | null>(null);
  const [products, setProducts] = useState<DraftProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [alert, setAlert] = useState<{ show: boolean; type: string; message: string }>({
    show: false,
    type: "success",
    message: "",
  });

  // Product form modal
  const [productModal, setProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<DraftProduct | null>(null);
  const [productForm, setProductForm] = useState<DraftProduct>({
    product_type: "plan",
    title: "",
    description: "",
    features: [],
    price: null,
    is_fixed_price: true,
    subscription_period: "month",
    cancellation_days: 0,
    quantifiable: false,
    share_percentage: 0,
    group: "",
    priority: 999,
  });
  const [featureInput, setFeatureInput] = useState("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [sharePercentageInput, setSharePercentageInput] = useState<string>("");

  // Submit confirmation modal
  const [submitModal, setSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Group management for PlanOptionals
  const [availableGroups, setAvailableGroups] = useState<string[]>([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);

  // Tab management
  const [activeTab, setActiveTab] = useState<string>("1");

  useEffect(() => {
    if (token) {
      fetchSubmission();
    }
  }, [token]);

  useEffect(() => {
    // Update available groups from existing PlanOptionals products
    const groups = products
      .filter((p) => p.product_type === "planoptionals" && p.group)
      .map((p) => p.group!)
      .filter((g, i, arr) => arr.indexOf(g) === i);
    setAvailableGroups(groups);
  }, [products]);

  useEffect(() => {
    // Ensure is_fixed_price is always true for product types other than plan and onetimeproduct
    if (productForm.product_type && productForm.product_type !== "plan" && productForm.product_type !== "onetimeproduct") {
      if (!productForm.is_fixed_price) {
        setProductForm((prev) => ({ ...prev, is_fixed_price: true }));
      }
    }
  }, [productForm.product_type]);

  const fetchSubmission = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.post(`/payments/partnerSubmission/${token}/`, {});
      if (response && response.status === "SENT") {
        setSubmission(response.submission);
        setProducts(response.products || []);
      } else {
        setError(response.error || "Failed to load submission");
      }
    } catch (error: any) {
      console.error("Error fetching submission:", error);
      const errorMsg = error.response?.data?.error || "Failed to load submission. Please check the link and try again.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type: string, message: string) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: "success", message: "" }), 5000);
  };

  const handleAddProduct = (productType: string) => {
    setEditingProduct(null);
    setProductForm({
      product_type: productType,
      title: "",
      description: "",
      features: [],
      price: null,
      is_fixed_price: true,
      subscription_period: productType === "addon" ? "" : "month",
      cancellation_days: 0,
      quantifiable: productType === "addon" || productType === "onetimeproduct" ? false : false,
      share_percentage: 0,
      group: "",
      priority: 999,
    });
    setSharePercentageInput("");
    setFeatureInput("");
    setFormErrors({});
    setShowNewGroupInput(false);
    setNewGroupName("");
    setProductModal(true);
  };

  const handleEditProduct = (product: DraftProduct) => {
    setEditingProduct(product);
    const editedProduct = { ...product };
    // If subscription addon, plan, or planoptionals has empty period, reset to "month"
    if ((product.product_type === "subscriptionaddon" || product.product_type === "plan" || product.product_type === "planoptionals") && product.subscription_period === "") {
      editedProduct.subscription_period = "month";
    }
    // Ensure is_fixed_price is true for product types other than plan and onetimeproduct
    if (product.product_type !== "plan" && product.product_type !== "onetimeproduct") {
      editedProduct.is_fixed_price = true;
    }
    setProductForm(editedProduct);
    setSharePercentageInput(product.share_percentage === 0 ? "" : product.share_percentage.toString());
    setFeatureInput("");
    setFormErrors({});
    setShowNewGroupInput(false);
    setNewGroupName("");
    setProductModal(true);
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      const response = await axios.delete(`/payments/partnerDraftProduct/${productId}/`, {
        data: { token },
        withCredentials: true,
      });
      if (response && response.status === "DELETED") {
        showAlert("success", "Product deleted successfully");
        fetchSubmission();
      } else {
        showAlert("danger", response.error || "Failed to delete product");
      }
    } catch (error: any) {
      console.error("Error deleting product:", error);
      showAlert("danger", error.response?.data?.error || "Failed to delete product");
    }
  };

  const movePriority = async (productType: string, productId: number, direction: 'up' | 'down') => {
    try {
      const response = await axios.post(
        "/payments/moveDraftProductPriority/",
        {
          token,
          product_type: productType,
          product_id: productId,
          direction: direction
        },
        { withCredentials: true }
      );
      
      if (response && (response.status === "MOVED" || response.status === "NO_CHANGE")) {
        if (response.status === "NO_CHANGE") {
          showAlert("info", response.message || "Product position unchanged");
        } else {
          showAlert("success", "Product priority updated");
        }
        fetchSubmission(); // Refresh the list
      } else {
        showAlert("danger", response.error || "Failed to update priority");
      }
    } catch (error: any) {
      console.error("Error moving product priority:", error);
      showAlert("danger", error.response?.data?.error || "Failed to update priority");
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setProductForm({
        ...productForm,
        features: [...productForm.features, featureInput.trim()],
      });
      setFeatureInput("");
    }
  };

  const removeFeature = (index: number) => {
    setProductForm({
      ...productForm,
      features: productForm.features.filter((_, i) => i !== index),
    });
  };

  const handleGroupSelect = (value: string) => {
    if (value === "__new__") {
      setShowNewGroupInput(true);
      setProductForm({ ...productForm, group: "" });
    } else {
      setShowNewGroupInput(false);
      setProductForm({ ...productForm, group: value });
    }
  };

  const handleSaveProduct = async () => {
    // Validate
    const errors: Record<string, string> = {};
    if (!productForm.title.trim()) {
      errors.title = "Title is required";
    }
    if (productForm.is_fixed_price && (productForm.price === null || productForm.price === undefined)) {
      errors.price = "Price is required when fixed price is selected";
    }
    if (productForm.is_fixed_price && productForm.price !== null && productForm.price < 0) {
      errors.price = "Price cannot be negative";
    }
    if (productForm.share_percentage < 0 || productForm.share_percentage > 100) {
      errors.share_percentage = "Share percentage must be between 0 and 100";
    }
    if (productForm.cancellation_days === null || productForm.cancellation_days === undefined || isNaN(productForm.cancellation_days)) {
      errors.cancellation_days = "Cancellation days is required";
    }
    if (productForm.cancellation_days < 0) {
      errors.cancellation_days = "Cancellation days cannot be negative";
    }
    if (productForm.product_type === "planoptionals" && !productForm.group?.trim()) {
      errors.group = "Group is required for Plan Optionals";
    }
    if (
      productForm.product_type === "subscriptionaddon" ||
      productForm.product_type === "plan" ||
      productForm.product_type === "planoptionals"
    ) {
      if (!productForm.subscription_period || !["month", "year"].includes(productForm.subscription_period)) {
        errors.subscription_period = "Subscription period is required and must be Monthly or Yearly";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setSaving(true);
      setFormErrors({});

      // Handle new group
      let finalGroup = productForm.group;
      if (showNewGroupInput && newGroupName.trim()) {
        finalGroup = newGroupName.trim();
        setAvailableGroups([...availableGroups, finalGroup]);
      }

      const payload: any = {
        token,
        product_type: productForm.product_type,
        title: productForm.title.trim(),
        description: productForm.description || "",
        features: productForm.features,
        is_fixed_price: productForm.is_fixed_price,
        price: productForm.is_fixed_price ? productForm.price : null,
        cancellation_days: productForm.cancellation_days,
        share_percentage: productForm.share_percentage,
        priority: productForm.priority,
      };

      if (productForm.product_type === "addon" || productForm.product_type === "onetimeproduct") {
        payload.quantifiable = productForm.quantifiable;
      }

      if (
        productForm.product_type === "subscriptionaddon" ||
        productForm.product_type === "plan" ||
        productForm.product_type === "planoptionals"
      ) {
        payload.subscription_period = productForm.subscription_period;
      }

      if (productForm.product_type === "planoptionals") {
        payload.group = finalGroup;
      }

      if (editingProduct?.id) {
        payload.product_id = editingProduct.id;
      }

      const response = await axios.post("/payments/partnerDraftProduct/", payload, {
        withCredentials: true,
      });

      if (response && response.status === "SAVED") {
        showAlert("success", editingProduct ? "Product updated successfully" : "Product created successfully");
        setProductModal(false);
        fetchSubmission();
      } else {
        showAlert("danger", response.error || "Failed to save product");
      }
    } catch (error: any) {
      console.error("Error saving product:", error);
      const errorMsg = error.response?.data?.error || "Failed to save product. Please try again.";
      showAlert("danger", errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitSubmission = async () => {
    try {
      setSubmitting(true);
      const response = await axios.post(
        "/payments/submitPartnerSubmission/",
        { token },
        { withCredentials: true }
      );

      if (response && response.status === "SUBMITTED") {
        showAlert("success", response.message || "Submission sent successfully!");
        setSubmitModal(false);
        fetchSubmission();
      } else {
        showAlert("danger", response.error || "Failed to submit");
      }
    } catch (error: any) {
      console.error("Error submitting:", error);
      const errorMsg = error.response?.data?.error || "Failed to submit. Please try again.";
      showAlert("danger", errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const calculateShareAmount = () => {
    if (productForm.price && productForm.share_percentage) {
      return (productForm.price * productForm.share_percentage) / 100;
    }
    return 0;
  };

  const getProductTypeLabel = (type: string) => {
    return PRODUCT_TYPES.find((pt) => pt.value === type)?.label || type;
  };

  // Transform products for preview
  const transformProductsForPreview = (): PreviewPlan[] => {
    const plans = products
      .filter((p) => p.product_type === "plan")
      .sort((a, b) => (a.priority || 999) - (b.priority || 999))
      .map((plan): PreviewPlan => {
        const planPrice = plan.is_fixed_price && plan.price !== null ? plan.price.toString() : null;
        const planValidity = plan.subscription_period || undefined;

        // Get addons (one-time addons)
        const addons: PreviewAddOn[] = products
          .filter((p) => p.product_type === "addon")
          .sort((a, b) => (a.priority || 999) - (b.priority || 999))
          .map((addon): PreviewAddOn => ({
            id: addon.id?.toString() || "",
            title: addon.title,
            description: addon.description || "",
            price: addon.is_fixed_price && addon.price !== null ? addon.price.toString() : null,
            validity: addon.subscription_period || undefined,
            features: addon.features || [],
            selected: false,
          }));

        // Get subscription addons
        const subscriptionAddOns: PreviewSubscriptionAddOn[] = products
          .filter((p) => p.product_type === "subscriptionaddon")
          .sort((a, b) => (a.priority || 999) - (b.priority || 999))
          .map((addon): PreviewSubscriptionAddOn => ({
            id: addon.id?.toString() || "",
            title: addon.title,
            description: addon.description || "",
            price: addon.is_fixed_price && addon.price !== null ? addon.price.toString() : null,
            period: addon.subscription_period || "month",
            features: addon.features || [],
            selected: false,
          }));

        // Get plan optionals (grouped)
        const planOptionalsWithDefault = (optionals: DraftProduct[]) => {
          const groups = Array.from(new Set(optionals.map(o => o.group).filter(Boolean)));
          return groups.flatMap(group => {
            const groupOptions = optionals.filter(o => o.group === group);
            let selectedIdx = groupOptions.findIndex(o => o.is_fixed_price && o.price === 0);
            if (selectedIdx === -1) selectedIdx = 0;
            return groupOptions.map((o, idx) => ({
              id: o.id?.toString() || "",
              group: o.group || "",
              title: o.title,
              description: o.description || "",
              price: o.is_fixed_price && o.price !== null ? o.price.toString() : null,
              validity: o.subscription_period || undefined,
              features: o.features || [],
              selected: idx === selectedIdx,
            }));
          });
        };

        const optionals: PreviewPlanOptionals[] = planOptionalsWithDefault(
          products.filter((p) => p.product_type === "planoptionals")
            .sort((a, b) => (a.priority || 999) - (b.priority || 999))
        );

        return {
          id: plan.id?.toString() || "",
          title: plan.title,
          description: plan.description || "",
          price: planPrice,
          validity: planValidity,
          features: plan.features || [],
          addons,
          subscriptionAddOns,
          optionals,
        };
      });

    return plans;
  };

  // Transform one-time products for preview
  const transformOneTimeProductsForPreview = (): PreviewOneTimeProduct[] => {
    return products
      .filter((p) => p.product_type === "onetimeproduct")
      .sort((a, b) => (a.priority || 999) - (b.priority || 999))
      .map((product): PreviewOneTimeProduct => ({
        id: product.id?.toString() || "",
        title: product.title,
        description: product.description || "",
        price: product.is_fixed_price && product.price !== null ? product.price.toString() : null,
        quantifiable: product.quantifiable || false,
        features: product.features || [],
      }));
  };

  // Navigation state for plans preview
  const [plansStartIndex, setPlansStartIndex] = useState(0);
  const PLANS_VISIBLE_COUNT = 3;

  const navigatePlansLeft = () => {
    setPlansStartIndex(Math.max(0, plansStartIndex - 1));
  };

  const navigatePlansRight = () => {
    const previewPlans = transformProductsForPreview();
    const maxStartIndex = Math.max(0, previewPlans.length - PLANS_VISIBLE_COUNT);
    setPlansStartIndex(Math.min(maxStartIndex, plansStartIndex + 1));
  };

  const getVisiblePlans = () => {
    const previewPlans = transformProductsForPreview();
    if (previewPlans.length <= PLANS_VISIBLE_COUNT) {
      return previewPlans;
    }
    return previewPlans.slice(plansStartIndex, plansStartIndex + PLANS_VISIBLE_COUNT);
  };

  const getVisiblePlansWithPlaceholders = (): (PreviewPlan | null)[] => {
    const visiblePlans = getVisiblePlans();
    const plansToShow: (PreviewPlan | null)[] = [...visiblePlans];
    // Add empty placeholders to always have 3 columns
    while (plansToShow.length < 3) {
      plansToShow.push(null);
    }
    return plansToShow.slice(0, 3);
  };

  // Reset navigation index when plans change
  useEffect(() => {
    const previewPlans = transformProductsForPreview();
    if (plansStartIndex >= previewPlans.length && previewPlans.length > 0) {
      setPlansStartIndex(Math.max(0, previewPlans.length - PLANS_VISIBLE_COUNT));
    } else if (previewPlans.length <= PLANS_VISIBLE_COUNT) {
      setPlansStartIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  if (loading) {
    return (
      <div className="page-content" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading your submission...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={6}>
              <Card>
                <CardBody className="text-center">
                  <AlertCircle size={48} className="text-danger mb-3" />
                  <h4>Error Loading Submission</h4>
                  <p className="text-muted">{error}</p>
                  <p className="text-muted small">Please check the link or contact the administrator.</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (!submission) {
    return null;
  }

  const isLocked = submission.is_locked || submission.status !== "draft";

  return (
    <React.Fragment>
      <div className="page-content" style={{ background: "#f8f9fa", minHeight: "100vh", paddingTop: "1rem" }}>
        <Container fluid>
          {/* Packages & Services Header */}
          <Row className="mb-0">
            <Col md={12}>
              <Card className="border-0 shadow-sm">
                <CardBody className="py-4">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <div 
                      style={{ 
                        background: "#12274e", 
                        borderRadius: "50%", 
                        width: "80px",
                        height: "80px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        padding: "8px"
                      }}
                    >
                      <img 
                        src={logo} 
                        alt="BizServe Logo" 
                        style={{ width: "80px", height: "auto", maxHeight: "80px", objectFit: "contain" }}
                      />
                    </div>
                    <h1 className="mb-0" style={{ fontSize: "2rem", fontWeight: 700 }}>
                      Your Packages & Services
                    </h1>
                  </div>
                  <p className="mb-0 text-muted" style={{ fontSize: "1rem" }}>
                    This is where you add all your products and services! Think of it like creating a menu for your business. 
                    You'll add things like your plans (like Basic Plan, Premium Plan), add-ons (extra features), and one-time products. 
                    Once you finish adding everything and submit it for review, our team will check it. After you're approved and onboarded, 
                    all these products will show up on your pricing page where your customers can see them and buy them. 
                    So take your time and add everything you want to offer!
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Header */}
          <Row className="mb-0">
            <Col md={12}>
              <Card className="border-0 shadow-sm">
                <CardBody className="py-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h2 className="mb-1">Hello, {submission.partner_name}!</h2>
                      <p className="text-muted mb-0">
                        Domain: <strong>{submission.domain_name}</strong>
                      </p>
                    </div>
                    <div className="text-end">
                      {submission.days_remaining < 7 && submission.days_remaining > 0 && (
                        <Badge color="warning" className="mb-2 d-block">
                          {submission.days_remaining} day{submission.days_remaining !== 1 ? "s" : ""} remaining
                        </Badge>
                      )}
                      <Badge color={submission.status === "draft" ? "info" : submission.status === "submitted" ? "warning" : "success"}>
                        {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                      </Badge>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mt-3" style={{paddingRight: "5rem"}}>
            <Col md={12}>
              {alert.show && (
                <Alert color={alert.type} style={{ marginTop: "5rem", marginRight: "5rem" }} className="mb-3" toggle={() => setAlert({ ...alert, show: false })}>
                  {alert.message}
                </Alert>
              )}

              {isLocked && (
                <Alert color="warning" style={{ marginRight: "5rem" }}>
                  <strong>Submission Locked:</strong> This submission has been submitted and can no longer be edited.
                </Alert>
              )}
            </Col>
          </Row>

          {/* Tabs Navigation */}
          <Nav tabs className="nav-tabs-custom mb-4">
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={classnames({ active: activeTab === "1" })}
                onClick={() => setActiveTab("1")}
              >
                Add Products
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={classnames({ active: activeTab === "2" })}
                onClick={() => setActiveTab("2")}
              >
                Preview
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={classnames({ active: activeTab === "3" })}
                onClick={() => setActiveTab("3")}
              >
                Instructions
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            {/* Add Products Tab */}
            <TabPane tabId="1">
          {/* Product Types Section */}
          {!isLocked && (
            <Row className="mb-4">
              <Col md={12}>
                <Card>
                  <CardHeader>
                    <h5 className="mb-0">Add Products</h5>
                    <small className="text-muted">Click a button below to add a new product of that type</small>
                  </CardHeader>
                  <CardBody>
                    <div className="d-flex flex-wrap gap-2">
                      {PRODUCT_TYPES.map((type) => (
                        <Button
                          key={type.value}
                          color="primary"
                          outline
                          onClick={() => handleAddProduct(type.value)}
                          className="mb-2 d-inline-flex align-items-center"
                        >
                          <Plus size={16} className="me-1" />
                          Add {type.label}
                        </Button>
                      ))}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Products List */}
          <Row>
            {PRODUCT_TYPES.map((type) => {
              const typeProducts = products.filter((p) => p.product_type === type.value);
              if (typeProducts.length === 0) return null;

              return (
                <Col md={12} key={type.value} className="mb-4">
                  <Card>
                    <CardHeader>
                      <h5 className="mb-0">{type.label} ({typeProducts.length})</h5>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        {typeProducts.map((product, productIdx) => (
                          <ProductCard
                            key={product.id}
                            product={product}
                            typeLabel={type.label}
                            showActions={true}
                            onEdit={handleEditProduct}
                            onDelete={handleDeleteProduct}
                            onMoveUp={product.id ? () => movePriority(type.value, product.id!, 'up') : undefined}
                            onMoveDown={product.id ? () => movePriority(type.value, product.id!, 'down') : undefined}
                            canMoveUp={productIdx > 0}
                            canMoveDown={productIdx < typeProducts.length - 1}
                            isLocked={isLocked}
                            subscriptionPeriods={SUBSCRIPTION_PERIODS}
                          />
                        ))}
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {/* Submit Button */}
          {!isLocked && products.length > 0 && (
            <Row className="mt-4 mb-4">
              <Col md={12} className="text-center">
                <Button
                  color="success"
                  size="lg"
                  onClick={() => setSubmitModal(true)}
                  className="px-5 d-inline-flex align-items-center"
                >
                  <Send size={20} className="me-2" />
                  Submit for Review
                </Button>
              </Col>
            </Row>
          )}
            </TabPane>

            {/* Preview Tab */}
            <TabPane tabId="2">
              {(() => {
                const previewPlans = transformProductsForPreview();
                if (previewPlans.length === 0) {
                  return (
                    <div className="text-center text-muted" style={{ padding: '24px 12px' }}>
                      No plans available for preview. Add some plans to see the preview.
                    </div>
                  );
                }

                return (
                  <>
                    {previewPlans.length > PLANS_VISIBLE_COUNT && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 16, gap: 8 }}>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={navigatePlansLeft}
                          disabled={plansStartIndex === 0}
                          style={{ minWidth: 40, padding: '6px 12px' }}
                        >
                          <i className="bx bx-chevron-left" style={{ fontSize: 20 }}></i>
                        </button>
                        <span style={{ fontSize: 14, color: '#666', minWidth: 80, textAlign: 'center' }}>
                          {plansStartIndex + 1}-{Math.min(plansStartIndex + PLANS_VISIBLE_COUNT, previewPlans.length)} of {previewPlans.length}
                        </span>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={navigatePlansRight}
                          disabled={plansStartIndex >= previewPlans.length - PLANS_VISIBLE_COUNT}
                          style={{ minWidth: 40, padding: '6px 12px' }}
                        >
                          <i className="bx bx-chevron-right" style={{ fontSize: 20 }}></i>
                        </button>
                      </div>
                    )}
                    <div style={{ width: '100%', overflowX: 'auto', marginTop: 20 }}>
                      <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                        <thead>
                          <tr>
                            <th style={{ width: 180, border: '1px solid #e0e0e0', background: 'transparent' }}></th>
                            {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                              if (!plan) {
                                return (
                                  <th key={`empty-${visibleIdx}`} style={{ width: 'calc((100% - 180px) / 3)', padding: 24, border: '1px solid #e0e0e0', textAlign: 'center', background: 'transparent' }}>
                                  </th>
                                );
                              }
                              const planIdx = plansStartIndex + visibleIdx;
                              return (
                                <th key={plan.id} style={{ width: 'calc((100% - 180px) / 3)', padding: 24, border: '1px solid #e0e0e0', textAlign: 'center', background: 'transparent' }}>
                                  <div style={{ fontWeight: 700, fontSize: 20, color: '#222', marginBottom: 4 }}>{plan.title}</div>
                                  <div style={{ fontSize: 13, color: '#555', marginBottom: 4, position: 'relative' }}>
                                    {plan.description && plan.description.length > 50 ? (
                                      <>
                                        <span>{plan.description.substring(0, 50)}</span>
                                        <span
                                          style={{ color: '#1976d2', cursor: 'pointer' }}
                                          onMouseEnter={e => {
                                            const tip = document.getElementById(`plan-desc-tip-${planIdx}`);
                                            if (tip) tip.style.display = 'block';
                                          }}
                                          onMouseLeave={e => {
                                            const tip = document.getElementById(`plan-desc-tip-${planIdx}`);
                                            if (tip) tip.style.display = 'none';
                                          }}
                                        >
                                          {' '}...more
                                        </span>
                                        <div
                                          id={`plan-desc-tip-${planIdx}`}
                                          style={{
                                            display: 'none',
                                            position: 'absolute',
                                            top: '100%',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            marginTop: 8,
                                            background: '#222',
                                            color: '#fff',
                                            padding: 10,
                                            borderRadius: 8,
                                            fontSize: 12,
                                            zIndex: 10,
                                            minWidth: 200,
                                            maxWidth: 300,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                          }}
                                        >
                                          <ReactMarkdown
                                              components={{
                                                  p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 13, color: '#555' }}>{children}</p>,
                                                  strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                                  em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                  ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ul>,
                                                  ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ol>,
                                                  li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 13, color: '#555' }}>{children}</li>,
                                                  a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                              }}
                                          >
                                              {cleanMarkdown(plan.description)}
                                          </ReactMarkdown>
                                        </div>
                                      </>
                                    ) : (
                                      <ReactMarkdown
                                          components={{
                                              p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 13, color: '#555' }}>{children}</p>,
                                              strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                              em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                              ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ul>,
                                              ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 13, color: '#555' }}>{children}</ol>,
                                              li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 13, color: '#555' }}>{children}</li>,
                                              a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                          }}
                                      >
                                          {cleanMarkdown(plan.description)}
                                      </ReactMarkdown>
                                    )}
                                  </div>
                                  <div style={{ fontWeight: 700, fontSize: 28, color: '#1976d2', marginBottom: 0 }}>
                                    {plan.price !== null && plan.price !== undefined ? (
                                      <>
                                        ${plan.price}
                                        {plan.validity && <span style={{ fontSize: 16, fontWeight: 500, color: '#666', marginLeft: 4 }}> / {plan.validity}</span>}
                                      </>
                                    ) : (
                                      <span style={{ fontSize: 16, fontWeight: 500, color: '#1976d2' }}>
                                        Contact for pricing
                                      </span>
                                    )}
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {/* Features Row */}
                          <tr>
                            <td style={{ fontWeight: 600, fontSize: 15, color: '#222', background: '#fafbfc', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle' }}>Features</td>
                            {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                              if (!plan) {
                                return (
                                  <td key={`empty-features-${visibleIdx}`} style={{ padding: 16, verticalAlign: 'top', background: '#fafbfc', border: '1px solid #e0e0e0' }}>
                                  </td>
                                );
                              }
                              return (
                                <td key={plan.id + '-features'} style={{ padding: 16, verticalAlign: 'top', background: '#fafbfc', border: '1px solid #e0e0e0' }}>
                                  <ul style={{ listStyle: 'disc', paddingLeft: 18, margin: 0, fontSize: 13, color: '#444' }}>
                                    {plan.features.map((f, i) => <li key={i}>{f}</li>)}
                                  </ul>
                                </td>
                              );
                            })}
                          </tr>
                          {/* Add-ons Row */}
                          {getVisiblePlans().some(plan => plan && plan.addons && plan.addons.length > 0) && (
                            <tr>
                              <td style={{ fontWeight: 600, fontSize: 15, color: '#222', background: '#fff', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle' }}>Add-ons</td>
                              {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                                if (!plan) {
                                  return (
                                    <td key={`empty-addons-${visibleIdx}`} style={{ padding: 16, background: '#fff', border: '1px solid #e0e0e0' }}>
                                    </td>
                                  );
                                }
                                const planIdx = plansStartIndex + visibleIdx;
                                return (
                                  <td key={plan.id + '-addons'} style={{ padding: 16, background: '#fff', border: '1px solid #e0e0e0' }}>
                                    {plan.price !== null && plan.price !== undefined ? (
                                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {plan.addons.map((addon, addonIdx) => (
                                          <div key={addon.id} style={{ position: 'relative', marginBottom: 4 }}>
                                            <span
                                              style={{
                                                padding: '4px 10px',
                                                borderRadius: 16,
                                                background: addon.selected ? '#f5f5f5' : '#fafbfc',
                                                color: '#222',
                                                border: addon.selected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                                fontWeight: 500,
                                                fontSize: 13,
                                                transition: 'all 0.2s',
                                                position: 'relative',
                                                display: 'inline-block',
                                                minWidth: 0
                                              }}
                                              onMouseEnter={e => {
                                                const tip = document.getElementById(`addon-tip-${planIdx}-${addonIdx}`);
                                                if (tip) tip.style.display = 'block';
                                              }}
                                              onMouseLeave={e => {
                                                const tip = document.getElementById(`addon-tip-${planIdx}-${addonIdx}`);
                                                if (tip) tip.style.display = 'none';
                                              }}
                                            >
                                              {addon.title} <span style={{ fontWeight: 400, fontSize: 12 }}>{addon.price !== null && addon.price !== undefined ? `$${addon.price}${addon.validity && addon.validity !== '' && addon.validity !== 'lifetime' && addon.validity !== 'custom' ? ` / ${addon.validity}` : ''}` : 'Contact'}</span>
                                            </span>
                                            <div
                                              id={`addon-tip-${planIdx}-${addonIdx}`}
                                              style={{
                                                display: 'none',
                                                position: 'absolute',
                                                top: 0,
                                                left: '105%',
                                                background: '#222',
                                                color: '#fff',
                                                padding: 6,
                                                borderRadius: 8,
                                                fontSize: 12,
                                                zIndex: 10,
                                                minWidth: 140,
                                                maxWidth: 220,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                              }}
                                            >
                                              <ReactMarkdown
                                                  components={{
                                                      p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 12, color: '#fff' }}>{children}</p>,
                                                      strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                                      em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                      ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 12, color: '#fff' }}>{children}</ul>,
                                                      ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 12, color: '#fff' }}>{children}</ol>,
                                                      li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 12, color: '#fff' }}>{children}</li>,
                                                      a: ({ children, href }) => <a href={href} style={{ color: '#90caf9', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                                  }}
                                              >
                                                  {cleanMarkdown(addon.description)}
                                              </ReactMarkdown>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: 13, color: '#888', textAlign: 'center' }}>—</div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          )}
                          {/* Subscription Add-ons Row */}
                          {getVisiblePlans().some(plan => plan && plan.subscriptionAddOns && plan.subscriptionAddOns.length > 0) && (
                            <tr>
                              <td style={{ fontWeight: 600, fontSize: 15, color: '#222', background: '#fff', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle' }}>Subscription Add-ons</td>
                              {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                                if (!plan) {
                                  return (
                                    <td key={`empty-subscription-addons-${visibleIdx}`} style={{ padding: 16, background: '#fff', border: '1px solid #e0e0e0' }}>
                                    </td>
                                  );
                                }
                                const planIdx = plansStartIndex + visibleIdx;
                                return (
                                  <td key={plan.id + '-subscription-addons'} style={{ padding: 16, background: '#fff', border: '1px solid #e0e0e0' }}>
                                    {plan.price !== null && plan.price !== undefined ? (
                                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {plan.subscriptionAddOns.map((addon, addonIdx) => (
                                          <div key={addon.id} style={{ position: 'relative', marginBottom: 4 }}>
                                            <span
                                              style={{
                                                padding: '4px 10px',
                                                borderRadius: 16,
                                                background: addon.selected ? '#f5f5f5' : '#fafbfc',
                                                color: '#222',
                                                border: addon.selected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                                fontWeight: 500,
                                                fontSize: 13,
                                                transition: 'all 0.2s',
                                                position: 'relative',
                                                display: 'inline-block',
                                                minWidth: 0
                                              }}
                                              onMouseEnter={e => {
                                                const tip = document.getElementById(`subscription-addon-tip-${planIdx}-${addonIdx}`);
                                                if (tip) tip.style.display = 'block';
                                              }}
                                              onMouseLeave={e => {
                                                const tip = document.getElementById(`subscription-addon-tip-${planIdx}-${addonIdx}`);
                                                if (tip) tip.style.display = 'none';
                                              }}
                                            >
                                              {addon.title} <span style={{ fontWeight: 400, fontSize: 12 }}>${addon.price} / {addon.period || 'month'}</span>
                                            </span>
                                            <div
                                              id={`subscription-addon-tip-${planIdx}-${addonIdx}`}
                                              style={{
                                                display: 'none',
                                                position: 'absolute',
                                                top: 0,
                                                left: '105%',
                                                background: '#222',
                                                color: '#fff',
                                                padding: 6,
                                                borderRadius: 8,
                                                fontSize: 12,
                                                zIndex: 10,
                                                minWidth: 140,
                                                maxWidth: 220,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                              }}
                                            >
                                              <ReactMarkdown
                                                  components={{
                                                      p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 12, color: '#fff' }}>{children}</p>,
                                                      strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                                      em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                      ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 12, color: '#fff' }}>{children}</ul>,
                                                      ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 12, color: '#fff' }}>{children}</ol>,
                                                      li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 12, color: '#fff' }}>{children}</li>,
                                                      a: ({ children, href }) => <a href={href} style={{ color: '#90caf9', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                                  }}
                                              >
                                                  {cleanMarkdown(addon.description)}
                                              </ReactMarkdown>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: 13, color: '#888', textAlign: 'center' }}>—</div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          )}
                          {/* Options Row */}
                          {getVisiblePlans().some(plan => plan && plan.optionals && plan.optionals.length > 0) && (
                            <tr>
                              <td style={{ fontWeight: 600, fontSize: 15, color: '#222', background: '#fafbfc', border: '1px solid #e0e0e0', padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle' }}>Options</td>
                              {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                                if (!plan) {
                                  return (
                                    <td key={`empty-options-${visibleIdx}`} style={{ padding: 16, background: '#fafbfc', border: '1px solid #e0e0e0' }}>
                                    </td>
                                  );
                                }
                                const planIdx = plansStartIndex + visibleIdx;
                                return (
                                  <td key={plan.id + '-options'} style={{ padding: 16, background: '#fafbfc', border: '1px solid #e0e0e0' }}>
                                    {plan.price !== null && plan.price !== undefined ? (
                                      <>
                                        {Array.from(new Set(plan.optionals.map(o => o.group))).map(group => (
                                          <div key={group} style={{ marginBottom: 8 }}>
                                            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 2, color: '#555' }}>{group}</div>
                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                              {plan.optionals.filter(o => o.group === group).map((option, optionIdx) => (
                                                <label key={option.id} style={{
                                                  padding: '4px 10px',
                                                  borderRadius: 16,
                                                  background: option.selected ? '#f5f5f5' : '#fafbfc',
                                                  color: '#222',
                                                  border: option.selected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                                  fontWeight: 500,
                                                  fontSize: 13,
                                                  transition: 'all 0.2s',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 4,
                                                  marginBottom: 2,
                                                  position: 'relative',
                                                }}
                                                  onMouseEnter={e => {
                                                    const tip = document.getElementById(`option-tip-${planIdx}-${group}-${optionIdx}`);
                                                    if (tip) tip.style.display = 'block';
                                                  }}
                                                  onMouseLeave={e => {
                                                    const tip = document.getElementById(`option-tip-${planIdx}-${group}-${optionIdx}`);
                                                    if (tip) tip.style.display = 'none';
                                                  }}
                                                >
                                                  <input
                                                    type="radio"
                                                    name={`option-group-${planIdx}-${group}`}
                                                    checked={option.selected}
                                                    onChange={() => {}}
                                                    style={{ display: 'none' }}
                                                  />
                                                  {option.title} <span style={{ fontWeight: 400, fontSize: 12 }}>${option.price}{option.validity && option.validity !== '' && option.validity !== 'lifetime' && option.validity !== 'custom' ? ` / ${option.validity}` : ''}</span>
                                                  <div
                                                    id={`option-tip-${planIdx}-${group}-${optionIdx}`}
                                                    style={{
                                                      display: 'none',
                                                      position: 'absolute',
                                                      top: 0,
                                                      left: '105%',
                                                      background: '#222',
                                                      color: '#fff',
                                                      padding: 6,
                                                      borderRadius: 8,
                                                      fontSize: 12,
                                                      zIndex: 10,
                                                      minWidth: 140,
                                                      maxWidth: 220,
                                                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                      whiteSpace: 'normal',
                                                      wordBreak: 'break-word',
                                                    }}
                                                  >
                                                    <ReactMarkdown
                                                        components={{
                                                            p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 12, color: '#fff' }}>{children}</p>,
                                                            strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                                            em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                                            ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 12, color: '#fff' }}>{children}</ul>,
                                                            ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 12, color: '#fff' }}>{children}</ol>,
                                                            li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 12, color: '#fff' }}>{children}</li>,
                                                            a: ({ children, href }) => <a href={href} style={{ color: '#90caf9', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                                        }}
                                                    >
                                                        {cleanMarkdown(option.description)}
                                                    </ReactMarkdown>
                                                  </div>
                                                </label>
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                      </>
                                    ) : (
                                      <div style={{ fontSize: 13, color: '#888', textAlign: 'center' }}>—</div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          )}
                          {/* Total Row */}
                          <tr>
                            <td style={{ fontWeight: 700, fontSize: 16, color: '#222', background: '#fff', border: '1px solid #e0e0e0', padding: '20px 20px', textAlign: 'center', verticalAlign: 'middle' }}>Total</td>
                            {getVisiblePlansWithPlaceholders().map((plan, visibleIdx) => {
                              if (!plan) {
                                return (
                                  <td key={`empty-total-${visibleIdx}`} style={{ padding: 20, background: '#fff', textAlign: 'center', border: '1px solid #e0e0e0' }}>
                                  </td>
                                );
                              }
                              const recurring = getPlanRecurring(plan);
                              const totalPrice = getPlanTotal(plan);
                              const hasNullPrice = totalPrice === null;
                              return (
                                <td key={plan.id + '-total'} style={{ padding: 20, background: '#fff', textAlign: 'center', border: '1px solid #e0e0e0' }}>
                                  <div style={{ fontWeight: 700, fontSize: 18, color: '#1976d2', marginBottom: 8 }}>
                                    {hasNullPrice ? (
                                      <span style={{ fontSize: 16, fontWeight: 500, color: '#1976d2' }}>
                                        Contact for pricing
                                      </span>
                                    ) : (
                                      `$${totalPrice}`
                                    )}
                                  </div>
                                  {Object.keys(recurring).length > 0 && (
                                    <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>
                                      {Object.entries(recurring).map(([period, amount]) => (
                                        <div key={period}>
                                          Per {period} from next {period}: <span style={{ fontWeight: 600, color: '#1976d2' }}>${amount.toFixed(2)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* One-Time Products Section */}
                    {(() => {
                      const oneTimeProducts = transformOneTimeProductsForPreview();
                      if (oneTimeProducts.length === 0) {
                        return null;
                      }

                      return (
                        <div style={{ marginTop: 40 }}>
                          <h3 style={{ fontWeight: 700, fontSize: 24, color: '#222', marginBottom: 24 }}>One-Time Products</h3>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
                            {oneTimeProducts.map(product => (
                              <div key={product.id} style={{
                                background: '#fff',
                                border: '1px solid #e0e0e0',
                                borderRadius: 12,
                                padding: 20,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.3s',
                              }}
                                onMouseEnter={e => {
                                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
                                  e.currentTarget.style.transform = 'translateY(-4px)';
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <h4 style={{ fontWeight: 700, fontSize: 18, marginBottom: 8, color: '#222' }}>{product.title}</h4>
                                <div style={{ fontWeight: 600, fontSize: 20, color: '#1976d2', marginBottom: 12 }}>
                                  {product.price !== null && product.price !== undefined ? `$${product.price}` : 'Contact for pricing'}
                                </div>
                                <div style={{ fontSize: 14, color: '#555', marginBottom: 12, flex: 1 }}>
                                  <ReactMarkdown
                                      components={{
                                          p: ({ children }) => <p style={{ marginBottom: '8px', marginTop: 0, fontSize: 14, color: '#555' }}>{children}</p>,
                                          strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
                                          em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                                          ul: ({ children }) => <ul style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 14, color: '#555' }}>{children}</ul>,
                                          ol: ({ children }) => <ol style={{ marginBottom: '8px', paddingLeft: '20px', fontSize: 14, color: '#555' }}>{children}</ol>,
                                          li: ({ children }) => <li style={{ marginBottom: '4px', fontSize: 14, color: '#555' }}>{children}</li>,
                                          a: ({ children, href }) => <a href={href} style={{ color: '#1976d2', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">{children}</a>,
                                      }}
                                  >
                                      {cleanMarkdown(product.description)}
                                  </ReactMarkdown>
                                </div>
                                {product.features && product.features.length > 0 && (
                                  <ul style={{ fontSize: 13, color: '#666', paddingLeft: 18, marginBottom: 16 }}>
                                    {product.features.map((feature, idx) => (
                                      <li key={idx}>{feature}</li>
                                    ))}
                                  </ul>
                                )}
                                {product.quantifiable && (
                                  <div style={{ fontSize: 12, color: '#888', fontStyle: 'italic', marginTop: 'auto' }}>
                                    Can be purchased in quantities
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </>
                );
              })()}
            </TabPane>

            {/* Instructions Tab */}
            <TabPane tabId="3">
              <Card>
                <CardBody style={{ padding: "2rem" }}>
                  <h2 className="mb-4" style={{ fontWeight: 700, color: "#222" }}>Product Types & Usage Guide</h2>
                  
                  {/* Overview Section */}
                  <div className="mb-5">
                    <h3 className="mb-3" style={{ fontWeight: 600, color: "#1976d2" }}>Overview</h3>
                    <p className="text-muted" style={{ fontSize: "1rem", lineHeight: "1.6" }}>
                      This guide explains the different product types available in the system, how customers purchase them, 
                      and best practices for setting up your product catalog. Understanding these concepts will help you 
                      create an effective pricing structure that meets your business needs.
                    </p>
                  </div>

                  {/* Product Types Section */}
                  <div className="mb-5">
                    <h3 className="mb-3" style={{ fontWeight: 600, color: "#1976d2" }}>Product Types Explained</h3>
                    
                    {PRODUCT_TYPES.map((type, idx) => (
                      <Card key={type.value} className="mb-3" style={{ border: "1px solid #e0e0e0" }}>
                        <CardHeader style={{ background: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
                          <h5 className="mb-0" style={{ fontWeight: 600, color: "#222" }}>
                            {idx + 1}. {type.label}
                          </h5>
                        </CardHeader>
                        <CardBody>
                          <p className="mb-2" style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                            <strong>Description:</strong> {type.description}
                          </p>
                          <div className="mt-3">
                            <strong style={{ color: "#555" }}>Key Characteristics:</strong>
                            <ul className="mt-2" style={{ paddingLeft: "1.5rem" }}>
                              {type.value === "plan" && (
                                <>
                                  <li>Main subscription products with recurring billing (monthly or yearly)</li>
                                  <li>Can have fixed pricing or "Contact for Pricing" option</li>
                                  <li>Customers select one plan as their primary subscription</li>
                                  <li>Customers can include add-ons, subscription add-ons, and plan optionals</li>
                                  <li>Total price includes the plan price plus selected add-ons/optionals</li>
                                  <li>When set as Contact for Pricing, the customer will contact you for pricing and you can set a specific price for this plan for that client</li>
                                </>
                              )}
                              {type.value === "subscriptionaddon" && (
                                <>
                                  <li>Additional features with recurring billing (monthly or yearly)</li>
                                  <li>Always requires fixed pricing</li>
                                  <li>Can be added to any plan</li>
                                  <li>Billing period must be the same as the main plan</li>
                                  <li>Renewed along with plan on a recurring basis</li>
                                </>
                              )}
                              {type.value === "planoptionals" && (
                                <>
                                  <li>Optional features grouped together (e.g., "Processing Speed: Slow, Medium, Fast")</li>
                                  <li>Customers can select only one option per group</li>
                                  <li>Always requires fixed pricing</li>
                                  <li>Billing period must be the same as the main plan</li>
                                  <li>Renewed along with plan on a recurring basis</li>
                                  <li>Must be assigned to a group name</li>
                                  <li>One option in each group is selected by default (usually the $0 option)</li>
                                </>
                              )}
                              {type.value === "addon" && (
                                <>
                                  <li>One-time add-ons that enhance existing products</li>
                                  <li>Always requires fixed pricing</li>
                                  <li>Can be added to plans during purchase</li>
                                  <li>Can be quantifiable (purchased in quantities)</li>
                                  <li>Charged once at the time of purchase and not renewed on a recurring basis</li>
                                </>
                              )}
                              {type.value === "onetimeproduct" && (
                                <>
                                  <li>Standalone products purchased once and not renewed on a recurring basis</li>
                                  <li>Can have fixed pricing or "Contact for Pricing" option</li>
                                  <li>Purchased separately from plans and not part of a package</li>
                                  <li>Can be quantifiable (purchased in quantities)</li>
                                  <li>No subscription or recurring billing</li>
                                  <li>Perfect for services like consultations, one-time reports, or implementation packages</li>
                                </>
                              )}
                            </ul>
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>

                  {/* How Products Are Purchased Section */}
                  <div className="mb-5">
                    <h3 className="mb-3" style={{ fontWeight: 600, color: "#1976d2" }}>How Products Are Purchased</h3>
                    
                    <Card className="mb-3" style={{ border: "1px solid #e0e0e0" }}>
                      <CardHeader style={{ background: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
                        <h5 className="mb-0" style={{ fontWeight: 600, color: "#222" }}>Package Purchases (Plans)</h5>
                      </CardHeader>
                      <CardBody>
                        <p style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                          When a customer purchases a <strong>Plan</strong>, they are buying a complete package that includes:
                        </p>
                        <ol style={{ paddingLeft: "1.5rem", fontSize: "0.95rem", lineHeight: "1.8" }}>
                          <li><strong>The Plan itself</strong> - The main subscription with its base price and features</li>
                          <li><strong>Add-ons (optional)</strong> - One-time add-ons that can be selected and added to the plan</li>
                          <li><strong>Subscription Add-ons (optional)</strong> - Recurring add-ons that are billed separately</li>
                          <li><strong>Plan Optionals (optional)</strong> - Optional features grouped together where one option per group can be selected</li>
                        </ol>
                        <div className="mt-3 p-3" style={{ background: "#f0f7ff", borderRadius: "8px", border: "1px solid #b3d9ff" }}>
                          <strong style={{ color: "#1976d2" }}>Example Package Purchase:</strong>
                          <p className="mb-0 mt-2" style={{ fontSize: "0.9rem" }}>
                            Customer selects "Premium Plan" ($99/month) + "Priority Support Add-on" ($50 one-time) + 
                            "Extra Storage Subscription" ($10/month) + "Processing Speed: Fast" ($20/month). 
                            Total: $99/month + $50 one-time + $10/month + $20/month = $129/month recurring + $50 one-time.
                          </p>
                        </div>
                      </CardBody>
                    </Card>

                    <Card className="mb-3" style={{ border: "1px solid #e0e0e0" }}>
                      <CardHeader style={{ background: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
                        <h5 className="mb-0" style={{ fontWeight: 600, color: "#222" }}>Separate Purchases (One-Time Products)</h5>
                      </CardHeader>
                      <CardBody>
                        <p style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                          <strong>One-Time Products</strong> are purchased independently and are not part of any plan package:
                        </p>
                        <ul style={{ paddingLeft: "1.5rem", fontSize: "0.95rem", lineHeight: "1.8" }}>
                          <li>They appear in a separate section on the pricing page</li>
                          <li>Customers can purchase them without subscribing to any plan</li>
                          <li>They can be purchased multiple times if quantifiable</li>
                          <li>No recurring charges - one-time payment only</li>
                        </ul>
                        <div className="mt-3 p-3" style={{ background: "#f0f7ff", borderRadius: "8px", border: "1px solid #b3d9ff" }}>
                          <strong style={{ color: "#1976d2" }}>Example One-Time Product:</strong>
                          <p className="mb-0 mt-2" style={{ fontSize: "0.9rem" }}>
                            "Custom Implementation Package" ($500) - A one-time service that can be purchased 
                            independently of any subscription plan.
                          </p>
                        </div>
                      </CardBody>
                    </Card>
                  </div>

                  {/* General Uses Section */}
                  <div className="mb-5">
                    <h3 className="mb-3" style={{ fontWeight: 600, color: "#1976d2" }}>General Uses & Best Practices</h3>
                    
                    <Card className="mb-3" style={{ border: "1px solid #e0e0e0" }}>
                      <CardHeader style={{ background: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
                        <h5 className="mb-0" style={{ fontWeight: 600, color: "#222" }}>When to Use Each Product Type</h5>
                      </CardHeader>
                      <CardBody>
                        <div className="mb-3">
                          <strong style={{ color: "#555" }}>Plans:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                            Use for your main subscription tiers (Basic, Premium, Enterprise). These are the core products 
                            that customers subscribe to on a recurring basis.
                          </p>
                        </div>
                        <div className="mb-3">
                          <strong style={{ color: "#555" }}>Subscription Add-ons:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                            Use for features that customers can add to any plan with recurring billing. Examples: 
                            Extra storage, advanced analytics, priority support subscriptions.
                          </p>
                        </div>
                        <div className="mb-3">
                          <strong style={{ color: "#555" }}>Plan Optionals:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                            Use when you want to offer customers a choice within a category. Group related options together. 
                            Examples: Processing speed (Slow/Medium/Fast), Data center location (US/EU/Asia), 
                            Support level (Standard/Priority/Enterprise).
                          </p>
                        </div>
                        <div className="mb-3">
                          <strong style={{ color: "#555" }}>Add-ons:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                            Use for one-time enhancements to plans. Examples: Setup fees, migration services, 
                            training sessions, one-time data imports.
                          </p>
                        </div>
                        <div>
                          <strong style={{ color: "#555" }}>One-Time Products:</strong>
                          <p className="mb-0 mt-1" style={{ fontSize: "0.95rem", lineHeight: "1.6" }}>
                            Use for standalone services that don't require a subscription. Examples: One-time consultations, 
                            custom reports, implementation packages, audit services.
                          </p>
                        </div>
                      </CardBody>
                    </Card>
                  </div>

                  {/* Best Way to Upload Plans Section */}
                  <div className="mb-4">
                    <h3 className="mb-3" style={{ fontWeight: 600, color: "#1976d2" }}>Best Way to Upload Plans</h3>
                    
                    <Card style={{ border: "1px solid #e0e0e0" }}>
                      <CardBody>
                        <ol style={{ paddingLeft: "1.5rem", fontSize: "0.95rem", lineHeight: "1.8" }}>
                          <li className="mb-3">
                            <strong>Start with Plans:</strong> Create your main subscription plans first (Basic, Premium, Enterprise, etc.). 
                            These form the foundation of your pricing structure.
                          </li>
                          <li className="mb-3">
                            <strong>Add Features:</strong> For each plan, add relevant features that describe what's included. 
                            This helps customers understand the value of each tier.
                          </li>
                          <li className="mb-3">
                            <strong>Set Pricing:</strong> Decide whether each plan should have fixed pricing or "Contact for Pricing". 
                            Fixed pricing works best for standard offerings, while "Contact for Pricing" is ideal for custom enterprise solutions.
                          </li>
                          <li className="mb-3">
                            <strong>Create Add-ons:</strong> Add any one-time add-ons that can enhance plans (setup fees, migrations, etc.).
                          </li>
                          <li className="mb-3">
                            <strong>Create Subscription Add-ons:</strong> Add recurring add-ons that customers can subscribe to 
                            in addition to their plan (extra storage, premium support, etc.).
                          </li>
                          <li className="mb-3">
                            <strong>Create Plan Optionals:</strong> Group related optional features together. Remember to:
                            <ul style={{ paddingLeft: "1.5rem", marginTop: "0.5rem" }}>
                              <li>Use descriptive group names (e.g., "Processing Speed", "Data Center Location")</li>
                              <li>Create multiple options within each group</li>
                              <li>Set one option as the default (usually $0 or the most common option)</li>
                            </ul>
                          </li>
                          <li className="mb-3">
                            <strong>Create One-Time Products:</strong> Add any standalone products that don't require a subscription.
                          </li>
                          <li className="mb-3">
                            <strong>Set Priorities:</strong> Use the priority system to control the order in which products appear. 
                            Lower numbers appear first. This helps you organize products in a logical order.
                          </li>
                          <li className="mb-3">
                            <strong>Review in Preview:</strong> Always check the Preview tab to see how your products will appear to customers. 
                            Make sure the pricing, features, and organization look correct.
                          </li>
                          <li>
                            <strong>Submit for Review:</strong> Once everything looks good, submit your plans for administrator review. 
                            You won't be able to make changes after submission, so double-check everything first.
                          </li>
                        </ol>
                        
                        <div className="mt-4 p-3" style={{ background: "#fff3cd", borderRadius: "8px", border: "1px solid #ffc107" }}>
                          <strong style={{ color: "#856404" }}>💡 Pro Tips:</strong>
                          <ul className="mb-0 mt-2" style={{ paddingLeft: "1.5rem", fontSize: "0.9rem" }}>
                            <li>Keep plan names clear and descriptive</li>
                            <li>Use consistent pricing structures across similar products</li>
                            <li>Make sure cancellation days are set appropriately for each product type</li>
                            <li>Test the preview thoroughly before submitting</li>
                            <li>Consider your target market when setting share percentages</li>
                          </ul>
                        </div>
                      </CardBody>
                    </Card>
                  </div>

                  {/* Additional Notes */}
                  <div className="mt-4 p-3" style={{ background: "#e7f3ff", borderRadius: "8px", border: "1px solid #1976d2" }}>
                    <strong style={{ color: "#1976d2" }}>📝 Important Notes:</strong>
                    <ul className="mb-0 mt-2" style={{ paddingLeft: "1.5rem", fontSize: "0.9rem" }}>
                      <li>All products require a title, cancellation days</li>
                      <li>You will receive your share of the client payment once the cancellation days are crossed</li>
                      <li>Fixed price products require a price value</li>
                      <li>Plan Optionals must be assigned to a group</li>
                      <li>Subscription Add-ons, Plans, and Plan Optionals require a subscription period (month or year)</li>
                      <li>Once submitted, you cannot edit your products - make sure everything is correct before submitting</li>
                    </ul>
                  </div>
                </CardBody>
              </Card>
            </TabPane>
          </TabContent>

          {/* Product Form Modal */}
          <Modal isOpen={productModal} toggle={() => setProductModal(false)} size="lg" centered className="rounded-4">
            <ModalHeader toggle={() => setProductModal(false)}>
              <div className="modal-title mt-0 font-size-16 text-dark">
                {editingProduct ? "Edit Product" : "Add Product"} - {getProductTypeLabel(productForm.product_type)}
              </div>
            </ModalHeader>
            <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <div className="mb-3">
                <Label>
                  Title <span className="text-danger">*</span>
                  <small className="text-muted d-block">Product name (max 100 characters)</small>
                </Label>
                <Input
                  type="text"
                  value={productForm.title}
                  onChange={(e) => setProductForm({ ...productForm, title: e.target.value })}
                  maxLength={100}
                  invalid={!!formErrors.title}
                />
                {formErrors.title && <FormFeedback>{formErrors.title}</FormFeedback>}
              </div>

              <div className="mb-3">
                <Label>
                  Description <span className="text-danger">*</span>
                  <small className="text-muted d-block">Detailed description of the product</small>
                </Label>
                <Input
                  type="textarea"
                  rows={3}
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                />
              </div>

              <div className="mb-3">
                <Label>
                  Features <span className="text-danger">*</span>
                  <small className="text-muted d-block">List of product features</small>
                </Label>
                <InputGroup>
                  <Input
                    type="text"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && addFeature()}
                    placeholder="Enter a feature and press Enter"
                  />
                  <Button color="primary" onClick={addFeature}>
                    Add
                  </Button>
                </InputGroup>
                {productForm.features.length > 0 && (
                  <div className="mt-2">
                    {productForm.features.map((feature, index) => (
                      <Badge key={index} color="primary" className="me-2 mb-2 d-inline-flex align-items-center" style={{ fontSize: "0.875rem", padding: "0.5rem" }}>
                        <span>{feature}</span>
                        <X
                          size={14}
                          className="ms-2"
                          style={{ cursor: "pointer" }}
                          onClick={() => removeFeature(index)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <Label>
                  Price Type <span className="text-danger">*</span>
                  <small className="text-muted d-block">
                    {productForm.product_type === "plan" || productForm.product_type === "onetimeproduct"
                      ? "Choose between fixed price or contact for pricing"
                      : "Fixed price is required for this product type"}
                  </small>
                </Label>
                <div className="d-flex gap-3">
                  <Label check>
                    <Input
                      type="radio"
                      checked={productForm.is_fixed_price}
                      onChange={() => setProductForm({ ...productForm, is_fixed_price: true })}
                      className="me-2"
                    />
                    Fixed Price
                  </Label>
                  {(productForm.product_type === "plan" || productForm.product_type === "onetimeproduct") && (
                    <Label check>
                      <Input
                        type="radio"
                        checked={!productForm.is_fixed_price}
                        onChange={() => setProductForm({ ...productForm, is_fixed_price: false, price: null })}
                        className="me-2"
                      />
                      Contact for Pricing
                    </Label>
                  )}
                </div>
              </div>

              <Row>
                {productForm.is_fixed_price && (
                  <Col md={6}>
                    <div className="mb-3">
                      <Label>
                        Price <span className="text-danger">*</span>
                        <small className="text-muted d-block">Product price in USD</small>
                      </Label>
                      <InputGroup>
                        <InputGroupText>$</InputGroupText>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={productForm.price === null || productForm.price === undefined ? "" : productForm.price}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                              setProductForm({ ...productForm, price: null });
                            } else {
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue)) {
                                setProductForm({ ...productForm, price: numValue });
                              }
                            }
                          }}
                          invalid={!!formErrors.price}
                        />
                      </InputGroup>
                      {formErrors.price && <FormFeedback>{formErrors.price}</FormFeedback>}
                    </div>
                  </Col>
                )}

                {(productForm.product_type === "subscriptionaddon" ||
                  productForm.product_type === "plan" ||
                  productForm.product_type === "planoptionals") && (
                  <Col md={productForm.is_fixed_price ? 6 : 12}>
                    <div className="mb-3">
                      <Label>
                        Subscription Period <span className="text-danger">*</span>
                        <small className="text-muted d-block">Billing frequency for this product</small>
                      </Label>
                      <div className="d-flex gap-3">
                        {SUBSCRIPTION_PERIODS.map((period) => (
                          <Label check key={period.value}>
                            <Input
                              type="radio"
                              name="subscription_period"
                              value={period.value}
                              checked={productForm.subscription_period === period.value}
                              onChange={(e) => setProductForm({ ...productForm, subscription_period: e.target.value })}
                              className="me-2"
                            />
                            {period.label}
                          </Label>
                        ))}
                      </div>
                      {formErrors.subscription_period && <FormFeedback>{formErrors.subscription_period}</FormFeedback>}
                    </div>
                  </Col>
                )}

                {(productForm.product_type === "addon" || productForm.product_type === "onetimeproduct") && (
                  <Col md={productForm.is_fixed_price ? 6 : 12}>
                    <div className="mb-3">
                      <Label check className="d-flex align-items-center">
                        <Input
                          type="checkbox"
                          checked={productForm.quantifiable}
                          onChange={(e) => setProductForm({ ...productForm, quantifiable: e.target.checked })}
                          className="me-2"
                        />
                        <div>
                          <span>Quantifiable</span>
                          <small className="text-muted d-block">Can be purchased in quantities (e.g., seats, units)</small>
                        </div>
                      </Label>
                    </div>
                  </Col>
                )}
              </Row>

              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>
                      Proposed Share Percentage
                      <small className="text-muted d-block">Your proposed share percentage (0-100%)</small>
                    </Label>
                    <InputGroup>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={sharePercentageInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSharePercentageInput(value);
                          // Update form state only if there's a valid number
                          if (value === "") {
                            setProductForm({ ...productForm, share_percentage: 0 });
                          } else {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue)) {
                              setProductForm({ ...productForm, share_percentage: numValue });
                            }
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value === "" || isNaN(parseFloat(value))) {
                            setSharePercentageInput("");
                            setProductForm({ ...productForm, share_percentage: 0 });
                          } else {
                            const numValue = parseFloat(value);
                            setSharePercentageInput(numValue.toString());
                            setProductForm({ ...productForm, share_percentage: numValue });
                          }
                        }}
                        invalid={!!formErrors.share_percentage}
                      />
                      <InputGroupText>%</InputGroupText>
                    </InputGroup>
                    {productForm.price && productForm.share_percentage > 0 && (
                      <div className="mt-2 text-success">
                        <strong>Your Share: ${calculateShareAmount().toFixed(2)} ({productForm.share_percentage}%)</strong>
                      </div>
                    )}
                    {formErrors.share_percentage && <FormFeedback>{formErrors.share_percentage}</FormFeedback>}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label>
                      Cancellation Days <span className="text-danger">*</span>
                      <small className="text-muted d-block">Number of days after purchase when product can be cancelled</small>
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={productForm.cancellation_days}
                      onChange={(e) => setProductForm({ ...productForm, cancellation_days: parseInt(e.target.value) || 0 })}
                      invalid={!!formErrors.cancellation_days}
                    />
                    {formErrors.cancellation_days && <FormFeedback>{formErrors.cancellation_days}</FormFeedback>}
                  </div>
                </Col>
              </Row>


              {productForm.product_type === "planoptionals" && (
                <div className="mb-3">
                  <Label>
                    Group <span className="text-danger">*</span>
                    <small className="text-muted d-block">Group name for organizing optionals</small>
                  </Label>
                  <Input
                    type="select"
                    value={showNewGroupInput ? "__new__" : productForm.group || ""}
                    onChange={(e) => handleGroupSelect(e.target.value)}
                    invalid={!!formErrors.group}
                  >
                    <option value="">Select or create a group</option>
                    {availableGroups.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                    <option value="__new__">+ Create New Group</option>
                  </Input>
                  {showNewGroupInput && (
                    <Input
                      type="text"
                      className="mt-2"
                      value={newGroupName}
                      onChange={(e) => {
                        setNewGroupName(e.target.value);
                        setProductForm({ ...productForm, group: e.target.value });
                      }}
                      placeholder="Enter new group name"
                    />
                  )}
                  {formErrors.group && <FormFeedback>{formErrors.group}</FormFeedback>}
                  <div className="mt-3">
                    <small className="text-muted d-block mb-2">Example:</small>
                    <img 
                      src={planOptionalsExample} 
                      alt="Plan Optionals Example" 
                      className="img-fluid rounded border"
                      style={{ maxWidth: "100%", height: "auto" }}
                    />
                  </div>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setProductModal(false)} disabled={saving}>
                Cancel
              </Button>
              <Button color="primary" onClick={handleSaveProduct} disabled={saving} className="d-inline-flex align-items-center">
                {saving ? "Saving..." : <><Save size={16} className="me-1" /> Save</>}
              </Button>
            </ModalFooter>
          </Modal>

          {/* Submit Confirmation Modal */}
          <Modal isOpen={submitModal} toggle={() => setSubmitModal(false)} centered className="rounded-4">
            <ModalHeader toggle={() => setSubmitModal(false)}>
              <div className="modal-title mt-0 font-size-16 text-dark">Submit for Review</div>
            </ModalHeader>
            <ModalBody>
              <p>Are you sure you want to submit your product plans for review?</p>
              <p className="text-muted small">
                Once submitted, you will not be able to make any changes. The administrator will review and approve your products.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setSubmitModal(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button color="success" onClick={handleSubmitSubmission} disabled={submitting} className="d-inline-flex align-items-center">
                {submitting ? "Submitting..." : <><Send size={16} className="me-1" /> Submit</>}
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PartnerPlanForm;
